// The questions and rules for the public servicing journey, kept apart from
// the Alpine store so they can be tested. officev3 checks everything again
// on submit (lib/servicing/website-service-request.ts); this only decides
// what to ask next and when to stop early.

export const OFFICE_PHONE = '0330 822 2795';

export function buildQuestions({ type, makes = ['Vaillant', 'Ideal'] } = {}) {
    const questions = [
        {
            id: 'kind',
            title: 'What kind of heat pump is it?',
            subtitle: '',
            options: [
                { value: 'air-to-water', label: 'Air to water', sub: 'A unit outside heats your radiators or underfloor, and your hot water' },
                { value: 'air-to-air', label: 'Air to air', sub: 'Blows warm or cool air indoors, like air conditioning' },
                { value: 'ground-source', label: 'Ground source', sub: 'Pipes buried in the garden or a borehole' },
                { value: 'hybrid', label: 'Hybrid', sub: 'Works alongside a gas or oil boiler' },
                { value: 'not-sure', label: 'Not sure', sub: '' }
            ]
        },
        {
            id: 'make',
            title: 'What make is it?',
            subtitle: 'The name is on the front or side of the outdoor unit.',
            options: [...makes.map(make => ({ value: make, label: make, sub: '' })), { value: 'other', label: 'Another make', sub: '' }]
        },
        {
            id: 'model',
            title: 'Do you know the model?',
            subtitle: 'It is on a sticker on the outdoor unit. You can skip this.',
            input: 'text',
            optional: true
        },
        {
            id: 'fitted',
            title: 'When was it fitted?',
            subtitle: 'A rough guess is fine.',
            options: [
                { value: 'under-2', label: 'In the last 2 years', sub: '' },
                { value: '2-to-4', label: '2 to 4 years ago', sub: '' },
                { value: '5-plus', label: '5 or more years ago', sub: '' },
                { value: 'not-sure', label: 'Not sure', sub: '' }
            ]
        },
        {
            id: 'lastServiced',
            title: 'When was it last serviced?',
            subtitle: '',
            options: [
                { value: 'within-year', label: 'In the last year', sub: '' },
                { value: 'over-year', label: 'Over a year ago', sub: '' },
                { value: 'never', label: 'Never', sub: '' },
                { value: 'not-sure', label: 'Not sure', sub: '' }
            ]
        },
        {
            id: 'cylinder',
            title: 'Do you have a hot water cylinder?',
            subtitle: 'A large hot water tank, usually in a cupboard. We service it on the same visit.',
            options: [
                { value: 'yes', label: 'Yes', sub: '' },
                { value: 'no', label: 'No', sub: '' },
                { value: 'unknown', label: 'Not sure', sub: '' }
            ]
        },
        {
            id: 'condition',
            title: 'Is it working properly?',
            subtitle: '',
            options: [
                { value: 'working', label: 'Working fine', sub: '' },
                { value: 'not-right', label: 'Working, but something is not right', sub: 'Noises, less heat, or an error code' },
                { value: 'not-working', label: 'Not working', sub: 'No heating or hot water' }
            ]
        }
    ];
    if (type === 'one-off') {
        questions.push({
            id: 'timePreference',
            title: 'When suits you?',
            subtitle: 'We will offer you a date and time to confirm.',
            options: [
                { value: 'As soon as possible', label: 'As soon as possible', sub: '' },
                { value: 'Mornings', label: 'Mornings', sub: '' },
                { value: 'Afternoons', label: 'Afternoons', sub: '' },
                { value: 'Any time', label: 'Any time', sub: '' }
            ]
        });
    }
    // `key` matches the estimate journey, so markup cloned from it binds as is.
    return questions.map(question => ({ ...question, key: question.id }));
}

/** Stops the journey early when we cannot help. Null to carry on. */
export function stopFor(questionId, value) {
    if (questionId === 'kind' && ['air-to-air', 'ground-source', 'hybrid'].includes(value)) return 'kind';
    if (questionId === 'make' && value === 'other') return 'make';
    if (questionId === 'condition' && value === 'not-working') return 'not-working';
    return null;
}

export function stopCopy(reason, { makes = ['Vaillant', 'Ideal'] } = {}) {
    const makeList = makes.length > 1 ? `${makes.slice(0, -1).join(', ')} and ${makes[makes.length - 1]}` : makes.join('');
    switch (reason) {
        case 'area':
            return { title: 'We do not cover your area yet', body: 'We are starting with a small area and growing it.', existingCustomer: true };
        case 'invalid-postcode':
            return { title: 'We could not read that postcode', body: 'Check it and try again.' };
        case 'kind':
            return { title: 'Sorry, we cannot service this one yet', body: 'We only service air to water heat pumps at the moment.' };
        case 'make':
            return { title: 'Sorry, we cannot service this one yet', body: `We only service ${makeList} heat pumps at the moment.` };
        case 'not-working':
            return { title: 'That sounds like a repair', body: `A service will not fix it. Call us on ${OFFICE_PHONE} and we will find out what is wrong.` };
        case 'terms':
            return { title: 'Our terms have changed', body: 'Refresh the page to see the latest version, then try again.' };
        default:
            return { title: 'Something went wrong', body: `Try again, or call us on ${OFFICE_PHONE}.` };
    }
}

export function formatPounds(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return `£${Number.isInteger(number) ? number : number.toFixed(2)}`;
}

/** The postcode at the end of a Postcoder summary line, when the result has none of its own. */
export function postcodeFrom(item) {
    if (item?.postcode) return item.postcode;
    const match = String(item?.summaryline ?? '').match(/([A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2})\s*$/i);
    return match ? match[1].toUpperCase() : '';
}

export function splitName(fullName) {
    const parts = String(fullName ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return null;
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}
